import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { finalize, Observable } from 'rxjs';

import { AumicoCoreProvider } from '../../../../aumico-lib-core-fe/src/lib';
import {
    CreateOrganizationChecklistDto,
    GetOrganizationChecklistDto,
    UpdateOrganizationChecklistDto,
} from '../interfaces/checklists';
import { CreateDefaultChecklistDto } from '../interfaces/checklists/create-default-checklist-dto.interface';
import { GetDefaultChecklistDto } from '../interfaces/checklists/get-default-checklist-dto.interface';
import { UpdateDefaultChecklistDto } from '../interfaces/checklists/update-default-checklist-dto.interface';
import { UsersConfig, UsersConfigService } from '../users.config';

@Injectable({
    providedIn: 'root',
})
export class ChecklistService {
    private server = '';
    private readonly apiUrl = 'checklists';

    constructor(
        private http: HttpClient,
        @Inject(UsersConfigService) config: UsersConfig,
        private readonly coreProvider: AumicoCoreProvider,
    ) {
        this.server = config.apiGateway;
    }

    getOrganizationChecklists(): Observable<GetOrganizationChecklistDto[]> {
        return this.http.get<GetOrganizationChecklistDto[]>(`${this.server}${this.apiUrl}/organization`);
    }

    addOrganizationChecklist(
        createChecklistDto: CreateOrganizationChecklistDto,
    ): Observable<GetOrganizationChecklistDto> {
        return this.http.post<GetOrganizationChecklistDto>(
            `${this.server}${this.apiUrl}/organization`,
            createChecklistDto,
        );
    }

    updateOrganizationChecklist(
        id: string,
        updateChecklistDto: UpdateOrganizationChecklistDto,
    ): Observable<GetOrganizationChecklistDto> {
        return this.http.put<GetOrganizationChecklistDto>(
            `${this.server}${this.apiUrl}/${id}/organization`,
            updateChecklistDto,
        );
    }

    getOrganizationChecklistById(id: string): Observable<GetOrganizationChecklistDto> {
        return this.http.get<GetOrganizationChecklistDto>(`${this.server}${this.apiUrl}/${id}/organization`);
    }

    deleteOrganizationChecklist(ids: string[]): Observable<void> {
        return this.http.delete<void>(`${this.server}${this.apiUrl}/organization`, {
            body: { ids },
        });
    }

    getDefaultChecklists(): Observable<GetDefaultChecklistDto[]> {
        return this.http.get<GetDefaultChecklistDto[]>(`${this.server}${this.apiUrl}/default`);
    }

    addDefaultChecklist(createChecklistDto: CreateDefaultChecklistDto): Observable<GetDefaultChecklistDto> {
        return this.http.post<GetDefaultChecklistDto>(`${this.server}${this.apiUrl}/default`, createChecklistDto);
    }

    updateDefaultChecklist(
        id: string,
        updateChecklistDto: UpdateDefaultChecklistDto,
    ): Observable<GetDefaultChecklistDto> {
        return this.http.put<GetDefaultChecklistDto>(`${this.server}${this.apiUrl}/${id}/default`, updateChecklistDto);
    }

    getDefaultChecklistById(id: string): Observable<GetDefaultChecklistDto> {
        return this.http.get<GetDefaultChecklistDto>(`${this.server}${this.apiUrl}/${id}/default`);
    }

    deleteDefaultChecklists(ids: string[]): Observable<void> {
        return this.http.delete<void>(`${this.server}${this.apiUrl}/default`, {
            body: { ids },
        });
    }

    getDefaultChecklistsTranslations$(locales: string[], untranslated: boolean) {
        const queryParams = `locales=${locales.join(',')}&untranslated=${untranslated}`;
        return this.http.get<GetDefaultChecklistDto[]>(
            `${this.server}${this.apiUrl}/default/translations?${queryParams}`,
        );
    }

    updateDefaultChecklistsTranslations(
        checklists: Partial<GetDefaultChecklistDto>[],
    ): Observable<Partial<GetDefaultChecklistDto>[]> {
        this.coreProvider.dispatchBlockingRequest('updateDefaultChecklistsTranslations');
        return this.http
            .patch<Partial<GetDefaultChecklistDto[]>>(`${this.server}${this.apiUrl}/default/translations`, checklists)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('updateDefaultChecklistsTranslations')));
    }
}
